import ExcelJS from 'exceljs'
import { downloadExcelWorkbook } from './atlasDownloadHelpers'
import { cleanExportDate } from './atlasJsonExportHelpers'

const ACTIVITY_TRACKER_COLUMNS = [
    { header: 'Study Number', key: 'studyID', width: 18, wrap: true, family: 'identifiers' },
    { header: 'Source System', key: 'sourceSystem', width: 18, wrap: true, family: 'identifiers' },
    { header: 'Title', key: 'title', width: 48, wrap: true, family: 'studyDetails' },
    { header: 'Status', key: 'status', width: 18, wrap: true, family: 'status' },
    { header: 'Objective', key: 'objective', width: 60, wrap: true, family: 'studyDetails' },
    { header: 'Study Type', key: 'studyType', width: 22, wrap: true, family: 'studyDetails' },
    { header: 'Start Date', key: 'startDate', width: 16, family: 'timeline' },
    { header: 'End Date', key: 'endDate', width: 16, family: 'timeline' },
    { header: 'Planned Start Date', key: 'plannedStartDate', width: 20, family: 'planning' },
    { header: 'Planned End Date', key: 'plannedEndDate', width: 20, family: 'planning' },
    { header: 'Function', key: 'function', width: 22, wrap: true, family: 'studyDetails' },
]

function fillByFamily(family) {
    const makeFill = (argb) => ({
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb },
    })

    switch (family) {
        case 'identifiers':
            return makeFill('ECECEC')
        case 'status':
            return makeFill('EDE4F7')
        case 'timeline':
            return makeFill('DCEBFA')
        case 'planning':
            return makeFill('FDF2D0')
        case 'studyDetails':
        default:
            return makeFill('EAF2F8')
    }
}

function styleHeaderRow(worksheet, columns) {
    const headerRow = worksheet.getRow(1)
    headerRow.height = 24

    columns.forEach((col, index) => {
        const cell = headerRow.getCell(index + 1)

        cell.font = {
            bold: true,
            color: { argb: '1F1F1F' },
        }

        cell.alignment = {
            vertical: 'middle',
            horizontal: 'center',
            wrapText: true,
        }

        cell.fill = fillByFamily(col.family)

        cell.border = {
            top: { style: 'thin', color: { argb: 'BFBFBF' } },
            left: { style: 'thin', color: { argb: 'BFBFBF' } },
            bottom: { style: 'thin', color: { argb: 'BFBFBF' } },
            right: { style: 'thin', color: { argb: 'BFBFBF' } },
        }
    })
}

function styleBodyRows(worksheet, columns) {
    const wrapMap = columns.map((c) => !!c.wrap)

    worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return

        row.height = 60

        row.eachCell((cell, colNumber) => {
            cell.alignment = {
                vertical: 'top',
                wrapText: !!wrapMap[colNumber - 1],
            }

            cell.border = {
                bottom: { style: 'thin', color: { argb: 'E0E0E0' } },
            }
        })
    })
}

function autosetWorksheetView(worksheet) {
    worksheet.views = [{ state: 'frozen', ySplit: 1 }]
}

function normalizeActivityForTracker(activity = {}) {
    return {
        studyID: activity?.studyNumber ?? activity?.studyID ?? activity?.studyId ?? activity?.study_id ?? '',
        sourceSystem: activity?.sourceSystem ?? activity?.source_system ?? activity?.dataSource ?? '',
        title: activity?.title ?? '',
        status: activity?.status ?? '',
        objective: activity?.description ?? '',
        studyType: activity?.studyType ?? activity?.study_type ?? '',
        startDate: cleanExportDate(activity?.startDate),
        endDate: cleanExportDate(activity?.endDate),
        plannedStartDate: cleanExportDate(
            activity?.plannedStartDate ?? activity?.plannedStart ?? activity?.planned_start_date
        ),
        plannedEndDate: cleanExportDate(activity?.plannedEndDate ?? activity?.plannedEnd ?? activity?.planned_end_date),
        function: activity?.function ?? '',
    }
}

function addActivitiesTrackerSheet(workbook, activities = []) {
    const worksheet = workbook.addWorksheet('Activities Tracker')

    autosetWorksheetView(worksheet)

    worksheet.columns = ACTIVITY_TRACKER_COLUMNS.map((col) => ({
        header: col.header,
        key: col.key,
        width: col.width,
    }))

    worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: ACTIVITY_TRACKER_COLUMNS.length },
    }

    const rows = (Array.isArray(activities) ? activities : []).map(normalizeActivityForTracker)

    rows.forEach((row) => worksheet.addRow(row))

    styleHeaderRow(worksheet, ACTIVITY_TRACKER_COLUMNS)
    styleBodyRows(worksheet, ACTIVITY_TRACKER_COLUMNS)

    return worksheet
}

export async function exportActivitiesTrackerToExcel(activities = [], filename = 'activities_tracker.xlsx', opts = {}) {
    const { workbookTitle = 'ATLAS Activities Tracker' } = opts

    const workbook = new ExcelJS.Workbook()

    workbook.creator = 'Arysana - Tyler Reinsch'
    workbook.lastModifiedBy = 'Arysana - Tyler Reinsch'
    workbook.created = new Date()
    workbook.modified = new Date()

    workbook.title = workbookTitle
    workbook.subject = 'ATLAS Activities Tracker Export'
    workbook.company = 'Arysana'
    workbook.manager = 'Arysana'
    workbook.keywords = 'Arysana, ATLAS, Activities, Evidence Planning'
    workbook.description = "Generated with Arysana's ATLAS"

    addActivitiesTrackerSheet(workbook, activities)

    await downloadExcelWorkbook(workbook, filename)
}

const RANKING_VALIDATION = ['-', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
const YES_NO_VALIDATION = ['-', 'N', 'Y']

const DISSEMINATION_COLORS = {
    border: 'BFBFBF',
    valuePillar: 'D9EAF7',
    external: 'E2F0D9',
    internal: 'EDE4F7',
    references: 'FFF2CC',
    evidenceGeneration: 'DDEBF7',
    notes: 'FCE4D6',
    supported: 'C6E0B4',
    suboptimal: 'FFE699',
    aspirational: 'F4CCCC',
    evidenceGapFont: 'C00000',
}

export async function exportDisseminationPrioritizationWorkbook(
    model,
    filename = 'ATLAS_dissemination_evidence_prioritization.xlsx',
    activitiesDisplayName = 'Activities'
) {
    const workbook = new ExcelJS.Workbook()

    const authorName = 'Arysana - Tyler Reinsch'

    workbook.creator = authorName
    workbook.lastModifiedBy = authorName
    workbook.created = new Date()
    workbook.modified = new Date()

    workbook.title = 'ATLAS Dissemination and Evidence Prioritization Export'
    workbook.subject = 'Value message, evidence gap, activity prioritization workbook'
    workbook.company = 'Arysana'
    workbook.manager = 'Arysana'
    workbook.keywords =
        'Arysana, ATLAS, Dissemination, Evidence, Value Messages, Evidence Gaps, Activities, Prioritization'
    workbook.description = "Generated with Arysana's ATLAS"

    const workbookGuideSheet = workbook.addWorksheet('Workbook Guide')

    const disseminationSheetMeta = addDisseminationPrioritizationSheet(workbook, model, activitiesDisplayName)

    populateDisseminationPrioritizationSummarySheet(
        workbookGuideSheet,
        model,
        disseminationSheetMeta,
        activitiesDisplayName
    )

    const scoringConfigMeta = addBetaScoringConfigSheet(workbook, model, disseminationSheetMeta)

    addBetaPriorityScoringSheet(workbook, model, disseminationSheetMeta, scoringConfigMeta)

    addActivitiesTrackerSheet(workbook, model?.activities || [])

    addDisseminationReferencesSheet(workbook, model?.references || [])

    await downloadExcelWorkbook(workbook, filename)
}

function addDisseminationPrioritizationSheet(workbook, model = {}) {
    const worksheet = workbook.addWorksheet('Dissemination Prioritization')

    const externalAudiences = model?.externalAudiences || []
    const internalAudiences = model?.internalAudiences || []
    const rows = model?.rows || []

    const referencesCol = 3
    const externalStartCol = referencesCol + 1
    const internalStartCol = externalStartCol + externalAudiences.length
    const evidenceGenStartCol = internalStartCol + internalAudiences.length
    const notesCol = evidenceGenStartCol + 7

    worksheet.views = [{ state: 'frozen', ySplit: 2, xSplit: 2 }]

    worksheet.columns = [
        { key: 'category', width: 24 },
        { key: 'support', width: 72 },
        { key: 'references', width: 52 },
        ...externalAudiences.map((aud) => ({ key: `external_${aud}`, width: 18 })),
        ...internalAudiences.map((aud) => ({ key: `internal_${aud}`, width: 18 })),
        { key: 'evidenceUnderDevelopment', width: 22 },
        { key: 'evidenceUnderConsideration', width: 24 },
        { key: 'completedActivities', width: 34 },
        { key: 'ongoingActivities', width: 34 },
        { key: 'plannedActivities', width: 34 },
        { key: 'suspendedActivities', width: 34 },
        { key: 'aspirationalEvidenceStatement', width: 52 },
        { key: 'notes', width: 52 },
    ]

    worksheet.getCell(1, 1).value = 'Value Pillars'
    worksheet.getCell(1, 2).value = 'Dissemination and Support'
    worksheet.getCell(1, referencesCol).value = 'References'

    if (externalAudiences.length > 0) {
        safeMergeCells(worksheet, 1, externalStartCol, 1, internalStartCol - 1)
        worksheet.getCell(1, externalStartCol).value = 'Potential Impact for External Stakeholders'
    }

    if (internalAudiences.length > 0) {
        safeMergeCells(worksheet, 1, internalStartCol, 1, evidenceGenStartCol - 1)
        worksheet.getCell(1, internalStartCol).value = 'Potential Impact for Internal Stakeholders'
    }

    safeMergeCells(worksheet, 1, evidenceGenStartCol, 1, evidenceGenStartCol + 6)
    worksheet.getCell(1, evidenceGenStartCol).value = 'Evidence Generation'

    worksheet.getCell(1, notesCol).value = 'Recommendations / Action Items / Notes'

    worksheet.getCell(2, 1).value = 'Category'
    worksheet.getCell(2, 2).value = 'Object'

    externalAudiences.forEach((aud, index) => {
        worksheet.getCell(2, externalStartCol + index).value = aud
    })

    internalAudiences.forEach((aud, index) => {
        worksheet.getCell(2, internalStartCol + index).value = aud
    })

    //TODO update to activitiesDisplayNames
    worksheet.getCell(2, referencesCol).value = 'Reference(s)'
    worksheet.getCell(2, evidenceGenStartCol).value = 'Evidence Under Development?'
    worksheet.getCell(2, evidenceGenStartCol + 1).value = 'Evidence Under Consideration?'
    worksheet.getCell(2, evidenceGenStartCol + 2).value = 'Completed Activities'
    worksheet.getCell(2, evidenceGenStartCol + 3).value = 'Ongoing / Active / Planned Activities'
    worksheet.getCell(2, evidenceGenStartCol + 4).value = 'Proposed / Ideation Activities'
    worksheet.getCell(2, evidenceGenStartCol + 5).value = 'Terminated / On Hold Activities'
    worksheet.getCell(2, evidenceGenStartCol + 6).value = 'Aspirational Evidence Statement'
    worksheet.getCell(2, notesCol).value = 'Notes'

    rows.forEach((row) => {
        const excelRow = []

        excelRow[0] = row?.category || ''
        excelRow[1] = row?.label || ''

        excelRow.push(formatMultilineList(row?.references, { numbered: true }))

        externalAudiences.forEach((aud) => {
            excelRow.push(row?.externalScores?.[aud] ?? '')
        })

        internalAudiences.forEach((aud) => {
            excelRow.push(row?.internalScores?.[aud] ?? '')
        })
        excelRow.push(row?.evidenceUnderDevelopment ?? '')
        excelRow.push(row?.evidenceUnderConsideration ?? '')
        excelRow.push(formatMultilineList(row?.completedActivities))
        excelRow.push(formatMultilineList(row?.ongoingActivities))
        excelRow.push(formatMultilineList(row?.plannedActivities))
        excelRow.push(formatMultilineList(row?.suspendedActivities))
        excelRow.push(row?.aspirationalEvidenceStatement ?? '')
        excelRow.push(row?.notes ?? '')

        const addedRow = worksheet.addRow(excelRow)

        row.__excelRowNumber = addedRow.number
        addedRow.__rowType = row?.rowType
        addedRow.__status = row?.status
    })

    styleDisseminationPrioritizationSheet(worksheet, {
        externalStartCol,
        internalStartCol,
        referencesCol,
        evidenceGenStartCol,
        notesCol,
    })

    const audienceScoreColumns = [
        ...numberRange(externalStartCol, internalStartCol - 1),
        ...numberRange(internalStartCol, evidenceGenStartCol - 1),
    ]

    applyRankingValidationToRowTypes(worksheet, audienceScoreColumns, ['valueMessage', 'evidenceGap'])

    applyAudienceScoreConditionalFormattingToRowTypes(worksheet, audienceScoreColumns, ['valueMessage'])

    applyYesNoValidation(worksheet, 3, worksheet.rowCount, [evidenceGenStartCol, evidenceGenStartCol + 1])

    return {
        sheetName: worksheet.name,
        externalStartCol,
        internalStartCol,
        evidenceGenStartCol,
        notesCol,
    }
}

function styleDisseminationPrioritizationSheet(worksheet, layout) {
    worksheet.getRow(1).height = 28
    worksheet.getRow(2).height = 36

    worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell) => {
            cell.alignment = {
                vertical: rowNumber <= 2 ? 'middle' : 'top',
                horizontal: rowNumber <= 2 ? 'center' : 'left',
                wrapText: true,
            }

            cell.border = {
                top: { style: 'thin', color: { argb: DISSEMINATION_COLORS.border } },
                left: { style: 'thin', color: { argb: DISSEMINATION_COLORS.border } },
                bottom: { style: 'thin', color: { argb: DISSEMINATION_COLORS.border } },
                right: { style: 'thin', color: { argb: DISSEMINATION_COLORS.border } },
            }

            if (rowNumber <= 2) {
                cell.font = {
                    bold: true,
                    color: { argb: '1F1F1F' },
                }
            }
        })
    })

    fillHeaderRange(worksheet, 1, 1, 2, DISSEMINATION_COLORS.valuePillar)
    fillHeaderRange(worksheet, 2, 2, 2, DISSEMINATION_COLORS.valuePillar)
    fillHeaderRange(worksheet, layout.externalStartCol, layout.internalStartCol - 1, 2, DISSEMINATION_COLORS.external)
    fillHeaderRange(worksheet, layout.internalStartCol, layout.referencesCol - 1, 2, DISSEMINATION_COLORS.internal)
    fillHeaderRange(worksheet, layout.referencesCol, layout.referencesCol, 2, DISSEMINATION_COLORS.references)
    fillHeaderRange(
        worksheet,
        layout.evidenceGenStartCol,
        layout.evidenceGenStartCol + 5,
        2,
        DISSEMINATION_COLORS.evidenceGeneration
    )
    fillHeaderRange(worksheet, layout.notesCol, layout.notesCol, 2, DISSEMINATION_COLORS.notes)

    for (let rowNumber = 3; rowNumber <= worksheet.rowCount; rowNumber++) {
        worksheet.getRow(rowNumber).getCell(layout.evidenceGenStartCol + 6).fill = solidFill('EAF4FF')
    }

    for (let rowNumber = 3; rowNumber <= worksheet.rowCount; rowNumber++) {
        const row = worksheet.getRow(rowNumber)
        const rowType = row.__rowType
        const status = row.__status

        row.height = rowType === 'valueNarrative' ? 34 : 58

        if (rowType === 'valueNarrative') {
            row.font = { bold: true }
            row.getCell(1).fill = solidFill(DISSEMINATION_COLORS.valuePillar)
            row.getCell(2).fill = solidFill(DISSEMINATION_COLORS.valuePillar)
        }

        if (rowType === 'valueMessage') {
            row.font = { bold: true }
            row.getCell(2).fill = solidFill(getValueMessageStatusFill(status))
        }

        if (rowType === 'kes') {
            row.getCell(2).fill = solidFill('FFFFFF')
            row.getCell(2).font = {
                color: { argb: '000000' },
                italic: false,
                bold: false,
            }

            clearAudienceScoreCellFills(row, layout)
        }

        if (rowType === 'evidenceGap') {
            row.getCell(2).fill = solidFill('FFFFFF')
            row.getCell(2).font = {
                color: { argb: DISSEMINATION_COLORS.evidenceGapFont },
                italic: true,
                bold: false,
            }

            clearAudienceScoreCellFills(row, layout)
        }
    }

    worksheet.autoFilter = {
        from: { row: 2, column: 1 },
        to: { row: 2, column: layout.notesCol },
    }
}

function addDisseminationReferencesSheet(workbook, references = []) {
    const worksheet = workbook.addWorksheet('References')

    worksheet.views = [{ state: 'frozen', ySplit: 1 }]

    worksheet.columns = [
        { header: '#', key: 'number', width: 8 },
        { header: 'Reference', key: 'reference', width: 120 },
    ]
    ;(references || []).forEach((reference, index) => {
        worksheet.addRow({
            number: index + 1,
            reference,
        })
    })

    worksheet.getRow(1).font = { bold: true }
    worksheet.getRow(1).height = 24

    worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell) => {
            cell.alignment = {
                vertical: rowNumber === 1 ? 'middle' : 'top',
                horizontal: rowNumber === 1 ? 'center' : 'left',
                wrapText: true,
            }

            cell.border = {
                bottom: { style: 'thin', color: { argb: DISSEMINATION_COLORS.border } },
            }

            if (rowNumber === 1) {
                cell.fill = solidFill(DISSEMINATION_COLORS.references)
            }
        })
    })

    return worksheet
}

function applyYesNoValidation(worksheet, startRow, endRow, columnNumbers) {
    for (let row = startRow; row <= endRow; row++) {
        for (const col of columnNumbers) {
            worksheet.getCell(row, col).dataValidation = {
                type: 'list',
                allowBlank: false,
                formulae: [`"${YES_NO_VALIDATION.join(',')}"`],
                showErrorMessage: true,
                errorTitle: 'Invalid value',
                error: 'Select one of: -, N, Y',
            }
        }
    }
}

function fillHeaderRange(worksheet, startCol, endCol, headerRows, argb) {
    if (endCol < startCol) return

    for (let row = 1; row <= headerRows; row++) {
        for (let col = startCol; col <= endCol; col++) {
            worksheet.getCell(row, col).fill = solidFill(argb)
        }
    }
}

function getValueMessageStatusFill(status = '') {
    const normalized = String(status).toLowerCase()

    if (normalized.includes('sub')) return DISSEMINATION_COLORS.suboptimal
    if (normalized.includes('aspirational')) return DISSEMINATION_COLORS.aspirational
    if (normalized.includes('support')) return DISSEMINATION_COLORS.supported

    return 'FFFFFF'
}

function formatMultilineList(values = [], opts = {}) {
    if (!Array.isArray(values)) return values || ''

    const cleaned = values.filter(Boolean)

    if (opts.numbered && cleaned.length > 1) {
        return cleaned.map((value, index) => `${index + 1}. ${value}`).join('\n')
    }

    if (opts.bulleted && cleaned.length > 0) {
        return cleaned.map((value) => `• ${value}`).join('\n')
    }

    return cleaned.join('\n')
}

function numberRange(start, end) {
    if (end < start) return []

    return Array.from({ length: end - start + 1 }, (_, index) => start + index)
}

function solidFill(argb) {
    return {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb },
    }
}

function addBetaPriorityScoringSheet(workbook, model = {}, disseminationSheetMeta = {}, scoringConfigMeta = {}) {
    const worksheet = workbook.addWorksheet('BETA_Priority_Scoring')

    worksheet.state = 'hidden'
    worksheet.views = [{ state: 'frozen', ySplit: 1 }]

    worksheet.columns = [
        { header: 'Object Type', key: 'objectType', width: 22 },
        { header: 'Object Name', key: 'objectName', width: 70 },
        { header: 'Parent Object', key: 'parentObject', width: 70 },
        { header: 'Linked Activities', key: 'linkedActivities', width: 60 },
        { header: 'Main Sheet Row', key: 'mainSheetRow', width: 16 },
        { header: 'Status', key: 'status', width: 18 },
        { header: 'External Impact', key: 'externalImpact', width: 18 },
        { header: 'Internal Impact', key: 'internalImpact', width: 18 },
        { header: 'Total Priority Score', key: 'totalPriorityScore', width: 22 },
        { header: 'Feasibility Score', key: 'feasibilityScore', width: 18 },
        { header: '2x2 X-Axis', key: 'xAxis', width: 14 },
        { header: '2x2 Y-Axis', key: 'yAxis', width: 14 },
        { header: '2x2 Quadrant', key: 'quadrant', width: 28 },
        { header: 'Include in Aspirational VM View?', key: 'aspirationalView', width: 30 },
        { header: 'Notes', key: 'notes', width: 50 },
    ]

    const rows = model?.rows || []
    const sheetName = disseminationSheetMeta?.sheetName || 'Dissemination Prioritization'

    const externalStartCol = disseminationSheetMeta?.externalStartCol
    const internalStartCol = disseminationSheetMeta?.internalStartCol
    const evidenceGenStartCol = disseminationSheetMeta?.evidenceGenStartCol

    const valueMessageRows = []
    const evidenceGapRows = []
    const activityScoreRows = []

    let currentValueNarrativeName = ''
    let currentValueMessageName = ''
    let currentValueMessageScoreCell = ''

    rows.forEach((row) => {
        const rowType = row?.rowType
        const mainRow = row?.__excelRowNumber

        if (rowType === 'valueNarrative') {
            currentValueNarrativeName = row?.label || ''
            return
        }

        if (!mainRow) return

        const externalFormula = getWeightedAudienceImpactFormula({
            sheetName,
            rowNumber: mainRow,
            audienceStartCol: externalStartCol,
            audienceEndCol: internalStartCol - 1,
            configStartRow: scoringConfigMeta?.externalWeightsStartRow,
        })

        const internalFormula = getWeightedAudienceImpactFormula({
            sheetName,
            rowNumber: mainRow,
            audienceStartCol: internalStartCol,
            audienceEndCol: evidenceGenStartCol - 1,
            configStartRow: scoringConfigMeta?.internalWeightsStartRow,
        })

        if (rowType === 'valueMessage') {
            currentValueMessageName = row?.label || ''

            valueMessageRows.push({
                row,
                mainRow,
                parentObject: currentValueNarrativeName,
                externalFormula,
                internalFormula,
            })
        }

        if (rowType === 'evidenceGap') {
            evidenceGapRows.push({
                row,
                mainRow,
                parentObject: currentValueMessageName,
                externalFormula,
                internalFormula,
            })
        }
    })

    addBetaSectionHeader(worksheet, 'Value Message Priority Scores')

    const valueMessageScoreCellByName = {}

    valueMessageRows.forEach(({ row, mainRow, parentObject, externalFormula, internalFormula }) => {
        const nextRowNumber = worksheet.rowCount + 1
        const totalFormula = `IFERROR(IF('SCORING_CONFIGURATION'!$B$2="Weighted Average",((G${nextRowNumber}*'SCORING_CONFIGURATION'!$B$3)+(H${nextRowNumber}*'SCORING_CONFIGURATION'!$B$4))/SUM('SCORING_CONFIGURATION'!$B$3:$B$4),(G${nextRowNumber}*'SCORING_CONFIGURATION'!$B$3)+(H${nextRowNumber}*'SCORING_CONFIGURATION'!$B$4)),0)`

        const added = worksheet.addRow({
            objectType: 'Value Message',
            objectName: row?.label || '',
            parentObject,
            linkedActivities: formatMultilineList(
                [
                    ...(row?.completedActivities || []),
                    ...(row?.ongoingActivities || []),
                    ...(row?.plannedActivities || []),
                    ...(row?.suspendedActivities || []),
                ],
                { bulleted: true }
            ),
            mainSheetRow: mainRow,
            status: row?.status || '',
            externalImpact: { formula: externalFormula },
            internalImpact: { formula: internalFormula },
            totalPriorityScore: { formula: totalFormula },
            feasibilityScore: '-',
            xAxis: { formula: `J${nextRowNumber}` },
            yAxis: { formula: `I${nextRowNumber}` },
            quadrant: {
                formula: `IF(OR(J${nextRowNumber}="-",J${nextRowNumber}=""),"Needs Feasibility",IF(AND(I${nextRowNumber}>=7,J${nextRowNumber}>=7),"High Impact / High Feasibility",IF(AND(I${nextRowNumber}>=7,J${nextRowNumber}<7),"High Impact / Lower Feasibility",IF(AND(I${nextRowNumber}<7,J${nextRowNumber}>=7),"Lower Impact / High Feasibility","Lower Impact / Lower Feasibility"))))`,
            },
            aspirationalView: String(row?.status || '')
                .toLowerCase()
                .includes('aspirational')
                ? 'Y'
                : 'N',
            notes: '',
        })

        valueMessageScoreCellByName[row?.label || ''] = `I${added.number}`
        ;[
            ...(row?.completedActivities || []),
            ...(row?.ongoingActivities || []),
            ...(row?.plannedActivities || []),
            ...(row?.suspendedActivities || []),
        ].forEach((activityTitle) => {
            activityScoreRows.push({
                activityTitle,
                valueMessageName: row?.label || '',
                valueMessageScoreCell: `I${added.number}`,
                status: inferActivityStatusFromBuckets(row, activityTitle),
            })
        })
    })

    addBetaSectionHeader(worksheet, 'Evidence Gap Priority Scores')

    evidenceGapRows.forEach(({ row, mainRow, parentObject, externalFormula, internalFormula }) => {
        const nextRowNumber = worksheet.rowCount + 1
        const totalFormula = `IFERROR(IF('SCORING_CONFIGURATION'!$B$2="Weighted Average",((G${nextRowNumber}*'SCORING_CONFIGURATION'!$B$3)+(H${nextRowNumber}*'SCORING_CONFIGURATION'!$B$4))/SUM('SCORING_CONFIGURATION'!$B$3:$B$4),(G${nextRowNumber}*'SCORING_CONFIGURATION'!$B$3)+(H${nextRowNumber}*'SCORING_CONFIGURATION'!$B$4)),0)`

        worksheet.addRow({
            objectType: 'Evidence Gap',
            objectName: row?.label || '',
            parentObject,
            linkedActivities: '',
            mainSheetRow: mainRow,
            status: '',
            externalImpact: { formula: externalFormula },
            internalImpact: { formula: internalFormula },
            totalPriorityScore: { formula: totalFormula },
            feasibilityScore: '-',
            xAxis: { formula: `J${nextRowNumber}` },
            yAxis: { formula: `I${nextRowNumber}` },
            quadrant: {
                formula: `IF(OR(J${nextRowNumber}="-",J${nextRowNumber}=""),"Needs Feasibility",IF(AND(I${nextRowNumber}>=7,J${nextRowNumber}>=7),"High Impact / High Feasibility",IF(AND(I${nextRowNumber}>=7,J${nextRowNumber}<7),"High Impact / Lower Feasibility",IF(AND(I${nextRowNumber}<7,J${nextRowNumber}>=7),"Lower Impact / High Feasibility","Lower Impact / Lower Feasibility"))))`,
            },
            aspirationalView: '',
            notes: 'Calculated from Evidence Gap row scores on main sheet',
        })
    })

    addBetaSectionHeader(worksheet, 'Activity Priority Scores')

    addActivityPriorityRowsToBetaSheet(worksheet, activityScoreRows, false)

    styleBetaPriorityScoringSheet(worksheet)

    return worksheet
}

function addActivityPriorityRowsToBetaSheet(worksheet, activityScoreRows = [], addHeader = true) {
    if (!activityScoreRows.length) return

    if (addHeader) {
        addBetaSectionHeader(worksheet, 'Activity Priority Scores')
    }

    const activityMap = new Map()

    activityScoreRows.forEach((row) => {
        if (!row?.activityTitle) return

        if (!activityMap.has(row.activityTitle)) {
            activityMap.set(row.activityTitle, {
                activityTitle: row.activityTitle,
                linkedValueMessages: [],
                scoreCells: [],
                statuses: [],
            })
        }

        const activity = activityMap.get(row.activityTitle)

        activity.linkedValueMessages.push(row.valueMessageName)
        activity.scoreCells.push(row.valueMessageScoreCell)
        activity.statuses.push(row.status)
    })

    activityMap.forEach((activity) => {
        const nextRowNumber = worksheet.rowCount + 1
        const scoreFormula = activity.scoreCells.length ? `IFERROR(SUM(${activity.scoreCells.join(',')}),0)` : '0'

        worksheet.addRow({
            objectType: 'Activity',
            objectName: activity.activityTitle,
            parentObject: dedupeList(activity.linkedValueMessages).join('\n'),
            linkedActivities: '',
            mainSheetRow: '',
            status: dedupeList(activity.statuses).join(', '),
            externalImpact: '',
            internalImpact: '',
            totalPriorityScore: { formula: scoreFormula },
            feasibilityScore: '-',
            xAxis: { formula: `J${nextRowNumber}` },
            yAxis: { formula: `I${nextRowNumber}` },
            quadrant: {
                formula: `IF(OR(J${nextRowNumber}="-",J${nextRowNumber}=""),"Needs Feasibility",IF(AND(I${nextRowNumber}>=7,J${nextRowNumber}>=7),"High Impact / High Feasibility",IF(AND(I${nextRowNumber}>=7,J${nextRowNumber}<7),"High Impact / Lower Feasibility",IF(AND(I${nextRowNumber}<7,J${nextRowNumber}>=7),"Lower Impact / High Feasibility","Lower Impact / Lower Feasibility"))))`,
            },
            aspirationalView: '',
            notes: 'Cumulative score of linked Value Messages',
        })
    })
}

function columnNumberToLetter(columnNumber) {
    let temp = columnNumber
    let letter = ''

    while (temp > 0) {
        const remainder = (temp - 1) % 26
        letter = String.fromCharCode(65 + remainder) + letter
        temp = Math.floor((temp - 1) / 26)
    }

    return letter
}

function inferActivityStatusFromBuckets(row = {}, activityTitle = '') {
    if ((row?.completedActivities || []).includes(activityTitle)) return 'Completed'
    if ((row?.ongoingActivities || []).includes(activityTitle)) return 'Ongoing'
    if ((row?.plannedActivities || []).includes(activityTitle)) return 'Planned'
    if ((row?.suspendedActivities || []).includes(activityTitle)) return 'Terminated / On Hold'

    return ''
}

function dedupeList(values = []) {
    return Array.from(new Set(values.filter(Boolean)))
}

function styleBetaPriorityScoringSheet(worksheet) {
    worksheet.getRow(1).font = { bold: true }
    worksheet.getRow(1).height = 28

    worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell) => {
            cell.alignment = {
                vertical: rowNumber === 1 ? 'middle' : 'top',
                horizontal: rowNumber === 1 ? 'center' : 'left',
                wrapText: true,
            }

            cell.border = {
                bottom: { style: 'thin', color: { argb: DISSEMINATION_COLORS.border } },
            }

            if (rowNumber === 1) {
                cell.fill = solidFill(DISSEMINATION_COLORS.notes)
            }
        })
    })

    applyBetaFeasibilityValidation(worksheet)
    applyBetaScoreConditionalFormatting(worksheet)
}

function applyAudienceScoreConditionalFormattingToRowTypes(worksheet, columnNumbers, allowedRowTypes = []) {
    for (let rowNumber = 3; rowNumber <= worksheet.rowCount; rowNumber++) {
        const row = worksheet.getRow(rowNumber)

        if (!allowedRowTypes.includes(row.__rowType)) continue

        for (const col of columnNumbers) {
            const colLetter = worksheet.getColumn(col).letter
            const range = `${colLetter}${rowNumber}:${colLetter}${rowNumber}`

            worksheet.addConditionalFormatting({
                ref: range,
                rules: [
                    {
                        type: 'containsText',
                        priority: 1,
                        operator: 'containsText',
                        text: '-',
                        style: {
                            fill: {
                                type: 'pattern',
                                pattern: 'solid',
                                bgColor: { argb: 'FFFFFF' },
                            },
                        },
                    },
                    {
                        type: 'cellIs',
                        priority: 2,
                        operator: 'greaterThanOrEqual',
                        formulae: ['8'],
                        style: {
                            fill: {
                                type: 'pattern',
                                pattern: 'solid',
                                bgColor: { argb: '6CA545' },
                            },
                            font: { color: { argb: 'FFFFFF' }, bold: true },
                        },
                    },
                    {
                        type: 'cellIs',
                        priority: 3,
                        operator: 'between',
                        formulae: ['6', '7'],
                        style: {
                            fill: {
                                type: 'pattern',
                                pattern: 'solid',
                                bgColor: { argb: 'A9D18E' },
                            },
                        },
                    },
                    {
                        type: 'cellIs',
                        priority: 4,
                        operator: 'equal',
                        formulae: ['5'],
                        style: {
                            fill: {
                                type: 'pattern',
                                pattern: 'solid',
                                bgColor: { argb: 'FFD966' },
                            },
                        },
                    },
                    {
                        type: 'cellIs',
                        priority: 5,
                        operator: 'between',
                        formulae: ['1', '4'],
                        style: {
                            fill: {
                                type: 'pattern',
                                pattern: 'solid',
                                bgColor: { argb: 'F4CCCC' },
                            },
                        },
                    },
                    {
                        type: 'cellIs',
                        priority: 6,
                        operator: 'equal',
                        formulae: ['0'],
                        style: {
                            fill: {
                                type: 'pattern',
                                pattern: 'solid',
                                bgColor: { argb: 'C00000' },
                            },
                            font: { color: { argb: 'FFFFFF' }, bold: true },
                        },
                    },
                ],
            })
        }
    }
}

function applyBetaScoreConditionalFormatting(worksheet) {
    const scoreColumns = [7, 8, 9, 10, 11, 12]

    scoreColumns.forEach((col) => {
        const colLetter = worksheet.getColumn(col).letter
        const range = `${colLetter}2:${colLetter}${worksheet.rowCount}`

        worksheet.addConditionalFormatting({
            ref: range,
            rules: [
                {
                    type: 'cellIs',
                    priority: 1,
                    operator: 'greaterThanOrEqual',
                    formulae: ['8'],
                    style: {
                        fill: {
                            type: 'pattern',
                            pattern: 'solid',
                            bgColor: { argb: '6CA545' },
                        },
                        font: { color: { argb: 'FFFFFF' }, bold: true },
                    },
                },
                {
                    type: 'cellIs',
                    priority: 2,
                    operator: 'between',
                    formulae: ['6', '7.999'],
                    style: {
                        fill: {
                            type: 'pattern',
                            pattern: 'solid',
                            bgColor: { argb: 'A9D18E' },
                        },
                    },
                },
                {
                    type: 'cellIs',
                    priority: 3,
                    operator: 'between',
                    formulae: ['4.5', '5.999'],
                    style: {
                        fill: {
                            type: 'pattern',
                            pattern: 'solid',
                            bgColor: { argb: 'FFD966' },
                        },
                    },
                },
                {
                    type: 'cellIs',
                    priority: 4,
                    operator: 'between',
                    formulae: ['1', '4.499'],
                    style: {
                        fill: {
                            type: 'pattern',
                            pattern: 'solid',
                            bgColor: { argb: 'F4CCCC' },
                        },
                    },
                },
                {
                    type: 'cellIs',
                    priority: 5,
                    operator: 'lessThan',
                    formulae: ['1'],
                    style: {
                        fill: {
                            type: 'pattern',
                            pattern: 'solid',
                            bgColor: { argb: 'C00000' },
                        },
                        font: { color: { argb: 'FFFFFF' }, bold: true },
                    },
                },
            ],
        })
    })
}

function safeMergeCells(worksheet, startRow, startCol, endRow, endCol) {
    if (!startRow || !startCol || !endRow || !endCol) return
    if (endRow < startRow || endCol < startCol) return

    // I was having issues with merging cells so adding to not merge a single cell.
    if (startRow === endRow && startCol === endCol) return

    try {
        worksheet.mergeCells(startRow, startCol, endRow, endCol)
    } catch (err) {
        console.warn('Skipping merge because range may already be merged:', {
            startRow,
            startCol,
            endRow,
            endCol,
            message: err?.message,
        })
    }
}

function applyRankingValidationToRowTypes(worksheet, columnNumbers, allowedRowTypes = []) {
    for (let rowNumber = 3; rowNumber <= worksheet.rowCount; rowNumber++) {
        const row = worksheet.getRow(rowNumber)

        if (!allowedRowTypes.includes(row.__rowType)) continue

        for (const col of columnNumbers) {
            worksheet.getCell(rowNumber, col).dataValidation = {
                type: 'list',
                allowBlank: false,
                formulae: [`"${RANKING_VALIDATION.join(',')}"`],
                showErrorMessage: true,
                errorTitle: 'Invalid value',
                error: 'Select one of: -, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10',
            }
        }
    }
}

function addBetaSectionHeader(worksheet, label) {
    const row = worksheet.addRow({
        objectType: label,
    })

    row.font = { bold: true }

    // Fill the entire row across all used columns
    for (let col = 1; col <= worksheet.columnCount; col++) {
        const cell = row.getCell(col)

        cell.fill = solidFill(DISSEMINATION_COLORS.notes)

        cell.border = {
            bottom: {
                style: 'thin',
                color: { argb: DISSEMINATION_COLORS.border },
            },
        }

        cell.alignment = {
            vertical: 'middle',
            horizontal: col === 1 ? 'left' : 'center',
            wrapText: true,
        }
    }

    return row
}

function clearAudienceScoreCellFills(row, layout) {
    const firstAudienceCol = layout.externalStartCol
    const lastAudienceCol = layout.evidenceGenStartCol - 1

    for (let col = firstAudienceCol; col <= lastAudienceCol; col++) {
        row.getCell(col).fill = solidFill('FFFFFF')
    }
}

function addBetaScoringConfigSheet(workbook, model = {}, disseminationSheetMeta = {}) {
    const worksheet = workbook.addWorksheet('SCORING_CONFIGURATION')
    worksheet.state = 'hidden'

    worksheet.columns = [
        { header: 'Parameter', key: 'parameter', width: 42 },
        { header: 'Value / Weight', key: 'weight', width: 18 },
        { header: 'Notes', key: 'notes', width: 90 },
    ]

    worksheet.addRow({
        parameter: 'Priority Score Calculation Mode',
        weight: 'Cumulative',
        notes: 'Options: Cumulative or Weighted Average. Cumulative adds weighted external and internal contributions. Weighted Average normalizes by total configured weight.',
    })

    worksheet.addRow({
        parameter: 'External Impact Weight',
        weight: 6,
        notes: 'Controls how strongly External Impact contributes to the priority score.',
    })

    worksheet.addRow({
        parameter: 'Internal Impact Weight',
        weight: 4,
        notes: 'Controls how strongly Internal Impact contributes to the priority score.',
    })

    worksheet.addRow({})

    worksheet.addRow({
        parameter: 'External Stakeholder Weights',
        weight: '',
        notes: '',
    })
    ;(model?.externalAudiences || []).forEach((audience) => {
        worksheet.addRow({
            parameter: audience,
            weight: 1,
            notes: 'Relative external stakeholder weight for future weighted stakeholder scoring.',
        })
    })

    worksheet.addRow({})

    worksheet.addRow({
        parameter: 'Internal Stakeholder Weights',
        weight: '',
        notes: '',
    })
    ;(model?.internalAudiences || []).forEach((audience) => {
        worksheet.addRow({
            parameter: audience,
            weight: 1,
            notes: 'Relative internal stakeholder weight for future weighted stakeholder scoring.',
        })
    })

    worksheet.getCell('B2').dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: ['"Cumulative,Weighted Average"'],
        showErrorMessage: true,
        errorTitle: 'Invalid scoring mode',
        error: 'Select Cumulative or Weighted Average.',
    }

    applyWeightValidation(worksheet, 3, worksheet.rowCount, [2])

    styleScoringConfigSheet(worksheet)

    const externalWeightsStartRow = 7
    const internalWeightsStartRow = externalWeightsStartRow + (model?.externalAudiences || []).length + 2

    return {
        sheetName: worksheet.name,
        scoringModeCell: `'${worksheet.name}'!$B$2`,
        externalWeightCell: `'${worksheet.name}'!$B$3`,
        internalWeightCell: `'${worksheet.name}'!$B$4`,
        externalWeightsStartRow,
        internalWeightsStartRow,
    }
}

function applyWeightValidation(worksheet, startRow, endRow, columnNumbers) {
    const WEIGHT_VALIDATION = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']

    for (let row = startRow; row <= endRow; row++) {
        for (const col of columnNumbers) {
            const cell = worksheet.getCell(row, col)

            if (cell.value === '') continue

            cell.dataValidation = {
                type: 'list',
                allowBlank: false,
                formulae: [`"${WEIGHT_VALIDATION.join(',')}"`],
                showErrorMessage: true,
                errorTitle: 'Invalid weight',
                error: 'Select a weight from 0 to 10.',
            }
        }
    }
}

function styleScoringConfigSheet(worksheet) {
    worksheet.getRow(1).font = { bold: true }
    worksheet.getRow(1).height = 28

    worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell, colNumber) => {
            cell.alignment = {
                vertical: 'top',
                wrapText: true,
            }

            cell.border = {
                bottom: { style: 'thin', color: { argb: DISSEMINATION_COLORS.border } },
            }

            if (rowNumber === 1) {
                cell.fill = solidFill(DISSEMINATION_COLORS.notes)
                cell.font = { bold: true }
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: 'center',
                    wrapText: true,
                }
            }

            if (colNumber === 2 && rowNumber > 1 && cell.value !== '') {
                cell.fill = solidFill('E2F0D9')
            }

            if (colNumber === 1 && typeof cell.value === 'string' && cell.value.includes('Weights')) {
                row.font = { bold: true }

                for (let col = 1; col <= worksheet.columnCount; col++) {
                    row.getCell(col).fill = solidFill(DISSEMINATION_COLORS.valuePillar)
                }
            }
        })
    })
}

function getWeightedAudienceImpactFormula({ sheetName, rowNumber, audienceStartCol, audienceEndCol, configStartRow }) {
    if (!audienceStartCol || !audienceEndCol || audienceEndCol < audienceStartCol) return '0'

    const scoreCells = []
    const weightCells = []

    for (let col = audienceStartCol; col <= audienceEndCol; col++) {
        const scoreColLetter = columnNumberToLetter(col)
        const weightRow = configStartRow + (col - audienceStartCol)

        scoreCells.push(`'${sheetName}'!${scoreColLetter}${rowNumber}`)
        weightCells.push(`'SCORING_CONFIGURATION'!$B$${weightRow}`)
    }

    const weightedNumerator = scoreCells.map((scoreCell, index) => `(${scoreCell}*${weightCells[index]})`).join('+')

    const weightedDenominator = weightCells.join('+')

    return `IFERROR((${weightedNumerator})/(${weightedDenominator}),0)`
}

function applyBetaFeasibilityValidation(worksheet) {
    const feasibilityCol = 10

    for (let rowNumber = 2; rowNumber <= worksheet.rowCount; rowNumber++) {
        const objectType = worksheet.getRow(rowNumber).getCell(1).value

        if (!['Value Message', 'Evidence Gap', 'Activity'].includes(objectType)) continue

        worksheet.getCell(rowNumber, feasibilityCol).dataValidation = {
            type: 'list',
            allowBlank: false,
            formulae: [`"${RANKING_VALIDATION.join(',')}"`],
            showErrorMessage: true,
            errorTitle: 'Invalid value',
            error: 'Select one of: -, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10',
        }
    }
}

function populateDisseminationPrioritizationSummarySheet(
    worksheet,
    model,
    disseminationSheetMeta,
    activitiesDisplayName
) {
    worksheet.columns = [{ width: 30 }, { width: 110 }]

    const externalStart = columnNumberToLetter(disseminationSheetMeta.externalStartCol)
    const externalEnd = columnNumberToLetter(disseminationSheetMeta.internalStartCol - 1)
    const internalStart = columnNumberToLetter(disseminationSheetMeta.internalStartCol)
    const internalEnd = columnNumberToLetter(disseminationSheetMeta.evidenceGenStartCol - 1)

    const evidenceDevCol = columnNumberToLetter(disseminationSheetMeta.evidenceGenStartCol)
    const evidenceConsiderationCol = columnNumberToLetter(disseminationSheetMeta.evidenceGenStartCol + 1)

    worksheet.getCell('A2').value = 'ATLAS Dissemination & Prioritization Workbook Guide'
    worksheet.getCell('A2').font = { bold: true, size: 16 }

    worksheet.getCell('A4').value = 'Generated With'
    worksheet.getCell('B4').value = "Arysana's ATLAS"

    worksheet.getCell('A5').value = 'Export Date'
    worksheet.getCell('B5').value = new Date().toLocaleString()

    worksheet.getCell('A6').value = 'Workbook Version'
    worksheet.getCell('B6').value = 'Beta'

    worksheet.getCell('A8').value = 'Purpose'
    worksheet.getCell('B8').value =
        'This workbook supports cross-functional dissemination planning, evidence prioritization, and activity prioritization workshops. It combines Value Narratives, Value Messages, Key Evidence Statements, Evidence Gaps, stakeholder impact scoring, evidence-generation status, and prototype prioritization scoring into a single workbook.'

    worksheet.getCell('A10').value = 'Workbook Overview'
    worksheet.getCell('B10').value =
        'Dissemination Prioritization: primary working sheet for reviewing narratives, messages, evidence, gaps, stakeholder impact, and evidence-generation status.\n\nBETA_Priority_Scoring: hidden scoring sheet that calculates priority metrics for Value Messages, Evidence Gaps, and Activities.\n\nSCORING_CONFIGURATION: hidden configuration sheet where users can adjust scoring mode and weights.\n\nActivities Tracker: supporting activity export.\n\nReferences: reference list used in the workbook.'

    worksheet.getCell('A12').value = 'Recommended Workflow'
    worksheet.getCell('B12').value =
        '1. Review Value Narratives, Value Messages, Key Evidence Statements, and Evidence Gaps on the Dissemination Prioritization sheet.\n\n2. Update stakeholder impact scores during the workshop. Value Message and Evidence Gap rows support 0–10 scoring.\n\n3. Review Evidence Under Development and Evidence Under Consideration fields to understand whether linked activities may address evidence needs.\n\n4. Unhide BETA_Priority_Scoring to review calculated priority scores.\n\n5. Unhide SCORING_CONFIGURATION to adjust scenario assumptions if desired.'

    worksheet.getCell('A14').value = 'Stakeholder Scoring'
    worksheet.getCell('B14').value =
        `External stakeholder scoring is located in columns ${externalStart}:${externalEnd}. Internal stakeholder scoring is located in columns ${internalStart}:${internalEnd}. Scores are initially populated as 10 when the audience/stakeholder is selected in ATLAS and 0 when not selected. Teams can update these values using the data validation dropdowns.`

    worksheet.getCell('A16').value = 'Evidence Generation'
    worksheet.getCell('B16').value =
        `Columns ${evidenceDevCol} and ${evidenceConsiderationCol} indicate whether evidence is under development or under consideration. These fields are automatically populated based on linked ${activitiesDisplayName} statuses such as Planned, Ongoing, Active, Initiated, Proposed, or Ideation.`

    worksheet.getCell('A18').value = 'Priority Scoring'
    worksheet.getCell('B18').value =
        'The hidden BETA_Priority_Scoring sheet calculates External Impact, Internal Impact, Total Priority Score, Feasibility Score, and 2x2 chart-ready fields. Value Messages and Evidence Gaps are scored from the stakeholder impact values. Activities use the cumulative priority score of linked Value Messages.'

    worksheet.getCell('A20').value = 'Scoring Configuration'
    worksheet.getCell('B20').value =
        'The hidden SCORING_CONFIGURATION sheet controls scoring assumptions. Users can choose Cumulative or Weighted Average scoring and adjust External Impact, Internal Impact, and stakeholder-specific weights.'

    worksheet.getCell('A22').value = 'How to Access Hidden Sheets'
    worksheet.getCell('B22').value =
        'Right-click any worksheet tab in Excel, select Unhide, then choose BETA_Priority_Scoring or SCORING_CONFIGURATION.'

    worksheet.getCell('A24').value = 'Scoring Example'
    worksheet.getCell('B24').value =
        'Example: If External Impact = 8, Internal Impact = 5, External Weight = 6, and Internal Weight = 4, then Cumulative scoring gives (8×6)+(5×4)=68. Weighted Average scoring gives ((8×6)+(5×4))/(6+4)=6.8. If an Activity supports three Value Messages with scores of 68, 40, and 25, its cumulative Activity score is 133.'

    worksheet.getCell('A26').value = 'Beta Notes'
    worksheet.getCell('B26').value =
        'This workbook is a prototype decision-support tool. Scores should support structured discussion and scenario planning, not replace strategic judgment.'

    for (let row = 1; row <= 26; row++) {
        const labelCell = worksheet.getCell(`A${row}`)
        const valueCell = worksheet.getCell(`B${row}`)

        if ([8, 10, 12, 14, 16, 18, 20, 22, 24, 26].includes(row)) {
            labelCell.fill = solidFill(DISSEMINATION_COLORS.valuePillar)
            labelCell.font = { bold: true }
        }

        labelCell.alignment = { vertical: 'top', wrapText: true }
        valueCell.alignment = { vertical: 'top', wrapText: true }

        labelCell.border = {
            bottom: { style: 'thin', color: { argb: 'E0E0E0' } },
        }
        valueCell.border = {
            bottom: { style: 'thin', color: { argb: 'E0E0E0' } },
        }
    }

    const footer = worksheet.getCell('B29')
    footer.value = "Generated with Arysana's ATLAS"
    footer.font = {
        italic: true,
        size: 10,
        color: { argb: '595959' },
    }
    footer.alignment = { horizontal: 'right' }

    return worksheet
}
